import { AuthSelectors, LanguageSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { PRIVACY_HIGHSCORE } from '../../base/constants';
import { getFriendsUrl, getUserPrivacySubpageUrl } from '../../base/historyService';
import { IState } from '../../store/state';
import { HIGHSCORES_LIST_SIZE } from '../constants';
import * as Selectors from '../selectors';
import { HighscoreFilter, IUserRanking } from '../types';
import AgreementPlaceholder from './AgreementPlaceholder';
import './HighscoresView.scss';
import InviteFriendsWidget from './InviteFriendsWidget';
import NoDataPlaceholder from './NoDataPlaceholder';
import UserRank, { IUserRankProps } from './UserRank';
import { getUserData } from './utils';

export interface IHighscoresViewProps {
  agreement: boolean;
  currentUser?: IUserRanking;
  filter: HighscoreFilter;
  highscores?: IUserRanking[];
  loaded: boolean;
  privacyUrl: string;
  inviteUrl: string;
}

export const HighscoresViewFC: React.FC<IHighscoresViewProps> = ({
  agreement,
  currentUser,
  filter,
  highscores = [],
  loaded,
  privacyUrl,
  inviteUrl,
}) => {
  const { t } = useTranslation();

  if (!currentUser || (loaded && !highscores.length)) {
    return <NoDataPlaceholder />;
  }
  const currentRankings: React.ReactNode[] = [];
  for (let i = 0; i < HIGHSCORES_LIST_SIZE; i += 1) {
    if (filter === 'friends' && highscores.length === i) {
      // add invite firends widget as last item if friends filter is on and not enough friends in the list
      currentRankings.push(<InviteFriendsWidget key={i} inviteUrl={inviteUrl} />);
    } else {
      const notEmpty = highscores.length > i;
      const isUser = loaded && notEmpty && highscores[i].uid === currentUser.uid;
      const ownRankName = t('Highscore.OwnRank');
      const { userName, picture, rankingScore, score } = getUserData(notEmpty, highscores[i], isUser, ownRankName);
      const ranking: IUserRankProps = {
        name: userName,
        picture: picture,
        ranking: rankingScore,
        score: score,
        active: isUser,
        loading: !loaded,
      };
      currentRankings.push(
        <div key={i} className="col-2">
          <UserRank {...ranking} />
        </div>
      );
    }
  }

  const ownRank = agreement ? (
    <div className="col-2">
      <UserRank
        name={t('Highscore.OwnRank')}
        picture={currentUser.picture}
        ranking={currentUser.ranking}
        score={currentUser.score}
        active={true}
        loading={!loaded}
      />
    </div>
  ) : (
    <div className="col-2">
      <AgreementPlaceholder picture={currentUser.picture} privacyUrl={privacyUrl} />
    </div>
  );

  return (
    <div className="highscores-view row">
      {currentRankings}
      <div className="highscores-spacer col-2">...</div>
      {ownRank}
    </div>
  );
};

export const mapStateToProps = (state: IState) => {
  // check if GDPR agreement is accepted
  const agreement = !!StatsSelectors.getStatBooleanValueById(state, PRIVACY_HIGHSCORE);
  // ranking for current user
  const currentUser = Selectors.getCurrentUser(state);
  // check if data was loaded
  const loaded = !!currentUser && !Selectors.isLoading(state);
  // rankings for other users
  const highscores = Selectors.getHighscores(state);
  // current filter
  const filter = Selectors.getFilter(state);
  // get information needed for privacy link
  const token = AuthSelectors.getToken(state);
  const lang = LanguageSelectors.getLanguage(state);
  const privacyUrl = getUserPrivacySubpageUrl(lang, token);
  const inviteUrl = getFriendsUrl(lang, token);
  return {
    agreement,
    currentUser,
    filter,
    highscores,
    loaded,
    privacyUrl,
    inviteUrl,
  };
};

export const mapDispatchToProps = {};

const HighscoresView = connect(mapStateToProps, mapDispatchToProps)(HighscoresViewFC);
HighscoresView.displayName = 'HighscoresView';
export default HighscoresView;
